import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Filter, X } from 'lucide-react';
import { formatInvCurrency } from './investmentUtils';

const INVESTMENT_TYPE_LABELS = {
  bank_balance: 'Bank Balance', fixed_deposit: 'Fixed Deposit', mutual_fund: 'Mutual Fund',
  treasury_bill: 'Treasury Bill', quoted_stocks: 'Quoted Stocks', government_bond: 'Government Bond',
  corporate_bond: 'Corporate Bond', private_equity: 'Private Equity', convertible: 'Convertible',
  safe: 'SAFE Investment', crypto: 'Cryptocurrency', real_estate: 'Real Estate (Property)', etf: 'ETF', gold: 'Gold'
};

const calculateGroupTotal = (investments) => investments.reduce((sum, inv) => sum + (inv.current_value || inv.amount || 0), 0);
const groupByInvType = (investments) => investments.reduce((acc, inv) => { const type = inv.type || 'other'; if (!acc[type]) acc[type] = []; acc[type].push(inv); return acc; }, {});

const GroupedByTypeSection = ({ investments, colorClass, renderRow, activeTypeFilter, onToggleType }) => {
  const byType = groupByInvType(investments);
  const entries = Object.entries(byType);
  // Filter at the section level — if the active filter type isn't present in this
  // section, the section simply renders nothing (the entity card stays visible
  // only because some other section may match; entity-level emptiness is handled
  // by the parent component).
  const visible = activeTypeFilter ? entries.filter(([type]) => type === activeTypeFilter) : entries;
  return visible.map(([type, typeInvestments]) => {
    const isActive = activeTypeFilter === type;
    const label = INVESTMENT_TYPE_LABELS[type] || type;
    const handle = () => onToggleType(type);
    return (
      <div key={type} className="mb-6 last:mb-0">
        <div
          role="button"
          tabIndex={0}
          onClick={handle}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handle(); } }}
          data-testid={`group-filter-${type}`}
          aria-pressed={isActive}
          aria-label={isActive ? `Click to show all investment types` : `Click to show only ${label}`}
          title={isActive ? 'Click to show all types' : `Click to show only ${label}`}
          className={`flex items-center justify-between py-2 px-4 ${colorClass} rounded-lg mb-3 cursor-pointer hover:brightness-95 transition focus:outline-hidden focus:ring-2 focus:ring-emerald-500 focus:ring-offset-1 ${isActive ? 'ring-2 ring-emerald-500' : ''}`}
        >
          <div className="flex items-center gap-3">
            <span className="font-semibold">{label}</span>
            <Badge variant="outline" className="bg-white">{typeInvestments.length} {typeInvestments.length === 1 ? 'investment' : 'investments'}</Badge>
          </div>
          <span className="font-bold font-mono" data-sensitive="monetary">{formatInvCurrency(calculateGroupTotal(typeInvestments))}</span>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="font-medium text-slate-700">Investment</TableHead>
              <TableHead className="font-medium text-slate-700 text-right">Invested</TableHead>
              <TableHead className="font-medium text-slate-700 text-right">Current Value</TableHead>
              <TableHead className="font-medium text-slate-700 text-right">P&L</TableHead>
              <TableHead className="font-medium text-slate-700">Details</TableHead>
              <TableHead className="font-medium text-slate-700 w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>{typeInvestments.map(inv => renderRow(inv))}</TableBody>
        </Table>
      </div>
    );
  });
};

const EntityGroupedView = ({ groupedInvestments, entities, relatedAssets, renderInvestmentRow }) => {
  const [activeTypeFilter, setActiveTypeFilter] = useState(null);
  const onToggleType = (type) => setActiveTypeFilter(prev => (prev === type ? null : type));

  const getEntityName = (entityId) => {
    const entity = entities.find(e => e.entity_id === entityId);
    const asset = relatedAssets.find(a => a.asset_id === entityId);
    return entity?.name || asset?.name || 'Unknown Entity';
  };
  const getEntityType = (entityId) => {
    const entity = entities.find(e => e.entity_id === entityId);
    const asset = relatedAssets.find(a => a.asset_id === entityId);
    return entity?.type || asset?.type || 'Unknown';
  };

  // Hide an entity card entirely when its investments don't include the filtered type.
  const sectionMatches = (list) => !activeTypeFilter || list.some(inv => (inv.type || 'other') === activeTypeFilter);
  // Total count across all sections — used in the filter pill copy.
  const totalCountForType = !activeTypeFilter ? 0 : (
    groupedInvestments.direct.filter(i => (i.type || 'other') === activeTypeFilter).length
    + Object.values(groupedInvestments.indirect).reduce((acc, list) => acc + list.filter(i => (i.type || 'other') === activeTypeFilter).length, 0)
    + Object.values(groupedInvestments.related).reduce((acc, list) => acc + list.filter(i => (i.type || 'other') === activeTypeFilter).length, 0)
  );

  // Reset filter when the data set changes (e.g., user adds/edits/deletes an investment).
  useEffect(() => {
    setActiveTypeFilter(prev => {
      if (!prev) return prev;
      const stillExists = (
        groupedInvestments.direct.some(i => (i.type || 'other') === prev)
        || Object.values(groupedInvestments.indirect).some(list => list.some(i => (i.type || 'other') === prev))
        || Object.values(groupedInvestments.related).some(list => list.some(i => (i.type || 'other') === prev))
      );
      return stillExists ? prev : null;
    });
  }, [groupedInvestments]);

  const filterBar = activeTypeFilter ? (
    <div
      className="flex items-center justify-between gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 sm:py-1.5 sticky top-0 z-30 shadow-xs"
      data-testid="grouped-view-filter-bar"
    >
      <div className="flex items-center gap-2 text-sm text-emerald-900 min-w-0">
        <Filter className="w-4 h-4 shrink-0" />
        <span className="truncate">
          Showing <span className="font-semibold">{INVESTMENT_TYPE_LABELS[activeTypeFilter] || activeTypeFilter}</span>
          <span className="hidden sm:inline text-emerald-700"> ({totalCountForType} {totalCountForType === 1 ? 'investment' : 'investments'})</span>
        </span>
      </div>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => setActiveTypeFilter(null)}
        className="h-7 px-2 text-emerald-800 hover:text-emerald-900 hover:bg-emerald-100 shrink-0"
        data-testid="grouped-view-clear-filter-btn"
        aria-label="Show all investment types"
      >
        <X className="w-4 h-4 mr-1" />Show all
      </Button>
    </div>
  ) : null;

  return (
    <div className="space-y-4">
      {filterBar}
      <div className="space-y-8">
      {groupedInvestments.direct.length > 0 && sectionMatches(groupedInvestments.direct) && (
        <Card className="portfolio-card">
          <CardHeader className="bg-emerald-100 border-emerald-200 rounded-t-lg">
            <div className="flex justify-between items-center">
              <div>
                <CardTitle className="text-xl font-serif text-emerald-900">Direct Portfolio</CardTitle>
                <p className="text-sm text-emerald-700 mt-1 font-mono font-bold">Total: <span data-sensitive="monetary">{formatInvCurrency(calculateGroupTotal(groupedInvestments.direct))}</span></p>
              </div>
              <Badge className="bg-emerald-500 text-white font-mono">{groupedInvestments.direct.length} {groupedInvestments.direct.length === 1 ? 'Investment' : 'Investments'}</Badge>
            </div>
          </CardHeader>
          <CardContent className="pt-6"><GroupedByTypeSection investments={groupedInvestments.direct} colorClass="bg-emerald-50" renderRow={renderInvestmentRow} activeTypeFilter={activeTypeFilter} onToggleType={onToggleType} /></CardContent>
        </Card>
      )}
      {Object.entries(groupedInvestments.indirect).filter(([, list]) => sectionMatches(list)).map(([entityId, entityInvestments]) => (
        <Card key={entityId} className="portfolio-card">
          <CardHeader className="bg-blue-100 border-blue-200 rounded-t-lg">
            <div className="flex justify-between items-center">
              <div>
                <CardTitle className="text-xl font-serif text-blue-900"><span data-sensitive="identifier">{getEntityName(entityId)}</span></CardTitle>
                <div className="flex items-center gap-3 mt-2">
                  <Badge className="bg-blue-500 text-white">{getEntityType(entityId)}</Badge>
                  <p className="text-sm text-blue-700 font-mono font-bold">Total: <span data-sensitive="monetary">{formatInvCurrency(calculateGroupTotal(entityInvestments))}</span></p>
                </div>
              </div>
              <Badge className="bg-blue-500 text-white font-mono">{entityInvestments.length} {entityInvestments.length === 1 ? 'Investment' : 'Investments'}</Badge>
            </div>
          </CardHeader>
          <CardContent className="pt-6"><GroupedByTypeSection investments={entityInvestments} colorClass="bg-blue-50" renderRow={renderInvestmentRow} activeTypeFilter={activeTypeFilter} onToggleType={onToggleType} /></CardContent>
        </Card>
      ))}
      {Object.entries(groupedInvestments.related).filter(([, list]) => sectionMatches(list)).map(([assetId, assetInvestments]) => (
        <Card key={assetId} className="portfolio-card border-indigo-200">
          <CardHeader className="bg-indigo-100 border-indigo-200 rounded-t-lg">
            <div className="flex justify-between items-center">
              <div>
                <CardTitle className="text-xl font-serif text-indigo-900"><span data-sensitive="identifier">{getEntityName(assetId)}</span></CardTitle>
                <div className="flex items-center gap-3 mt-2">
                  <Badge className="bg-indigo-500 text-white">{getEntityType(assetId)} - Memorandum Only</Badge>
                  <p className="text-sm text-indigo-700 font-mono font-bold">Total: <span data-sensitive="monetary">{formatInvCurrency(calculateGroupTotal(assetInvestments))}</span></p>
                </div>
              </div>
              <Badge className="bg-indigo-500 text-white font-mono">{assetInvestments.length} {assetInvestments.length === 1 ? 'Asset' : 'Assets'}</Badge>
            </div>
          </CardHeader>
          <CardContent className="pt-6"><GroupedByTypeSection investments={assetInvestments} colorClass="bg-indigo-50" renderRow={renderInvestmentRow} activeTypeFilter={activeTypeFilter} onToggleType={onToggleType} /></CardContent>
        </Card>
      ))}
      </div>
    </div>
  );
};

export default EntityGroupedView;
